import React, { useEffect, useRef, useState } from "react";
import { fetchBlob, fileType, parseCsv } from "./fileTypes";

/* Card thumbnail: an image shows itself, a PDF its first page (the browser's own
   PDF viewer in a scaled-down iframe), a CSV its first rows; anything else, or
   anything that fails, shows a coloured type badge.

   Kept light on purpose: nothing loads until the card scrolls into view, at most
   two previews load at a time, files over 5 MB stay a badge, and results are
   cached for the session so reopening a ticket doesn't fetch them again. */

const MAX_PREVIEW_BYTES = 5 * 1024 * 1024;
const MAX_PARALLEL = 2;
const CSV_ROWS = 6;
const CSV_COLS = 4;

const MAX_CACHED = 30;
const cache = new Map(); // cacheKey → { blobUrl } | { rows } | { failed: true }, oldest first
const remember = (key, value) => {
  cache.set(key, value);
  while (cache.size > MAX_CACHED) {
    const [oldKey, old] = cache.entries().next().value;
    if (old?.blobUrl) URL.revokeObjectURL(old.blobUrl);
    cache.delete(oldKey);
  }
};
const queue = [];
let running = 0;

const finished = () => { running -= 1; pump(); };
function pump() {
  if (running >= MAX_PARALLEL || !queue.length) return;
  const next = queue.shift();
  running += 1;
  next.job().then(next.resolve, next.reject).finally(finished);
  pump();
}

// Runs `job` when a slot is free (at most MAX_PARALLEL at once).
const schedule = (job) => new Promise((resolve, reject) => {
  queue.push({ job, resolve, reject });
  pump();
});

const loadPreview = async (kind, name, getUrl) => {
  const blob = await fetchBlob(await getUrl(), name);
  if (blob.size > MAX_PREVIEW_BYTES) return { failed: true };
  if (kind === "csv") return { rows: parseCsv((await blob.text()).slice(0, 20000)).slice(0, CSV_ROWS) };
  return { blobUrl: URL.createObjectURL(blob) };
};

// True once the element has been on screen (with a little look-ahead).
const useSeen = (ref) => {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen || !ref.current) return undefined;
    if (typeof IntersectionObserver === "undefined") { setSeen(true); return undefined; }
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) { setSeen(true); io.disconnect(); }
    }, { rootMargin: "120px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref, seen]);
  return seen;
};

const FileThumb = ({ name, url, getUrl, cacheKey }) => {
  const type = fileType(name);
  const boxRef = useRef(null);
  const seen = useSeen(boxRef);
  // Callers pass a new arrow each render; keep the latest without refetching.
  const getUrlRef = useRef(getUrl);
  getUrlRef.current = getUrl;

  // Images: the presigned URL is enough for an <img>.
  const [imgSrc, setImgSrc] = useState(type.kind === "image" ? url || null : null);
  const [imgFailed, setImgFailed] = useState(false);
  // PDF / CSV: fetched preview, shared through the module cache.
  const key = cacheKey ? `${cacheKey}|${name}` : null;
  const [preview, setPreview] = useState(() => (key && cache.get(key)) || null);

  useEffect(() => {
    if (!seen || type.kind !== "image" || imgSrc || imgFailed || !getUrlRef.current) return undefined;
    let live = true;
    getUrlRef.current().then(u => { if (live) setImgSrc(u); }).catch(() => { if (live) setImgFailed(true); });
    return () => { live = false; };
  }, [seen, type.kind, imgSrc, imgFailed]);

  useEffect(() => {
    if (!seen || preview || !key || !getUrlRef.current || (type.kind !== "pdf" && type.kind !== "csv")) return undefined;
    let live = true;
    schedule(() => loadPreview(type.kind, name, getUrlRef.current))
      .catch(() => ({ failed: true }))
      .then(result => { remember(key, result); if (live) setPreview(result); });
    return () => { live = false; };
  }, [seen, preview, key, type.kind, name]);

  const onImgError = () => {
    // A list URL may have expired: try a fresh one once, then give up.
    if (imgSrc && imgSrc === url && getUrlRef.current) setImgSrc(null);
    else setImgFailed(true);
  };

  let body = null;
  if (type.kind === "image" && imgSrc && !imgFailed) {
    body = <img src={imgSrc} alt="" loading="lazy" onError={onImgError} />;
  } else if (type.kind === "pdf" && preview?.blobUrl) {
    body = (
      <span className="att-thumb-pdf" aria-hidden="true">
        <iframe src={`${preview.blobUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} title={name} tabIndex={-1} loading="lazy" />
      </span>
    );
  } else if (type.kind === "csv" && preview?.rows?.length) {
    body = (
      <table className="att-thumb-sheet" aria-hidden="true">
        <tbody>
          {preview.rows.map((r, i) => (
            <tr key={i}>{Array.from({ length: CSV_COLS }, (_, j) => <td key={j}>{r[j] || ""}</td>)}</tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <span ref={boxRef} className="att-thumb">
      <span className="att-card__badge" style={{ background: type.color }}>{type.label}</span>
      {body}
    </span>
  );
};

export default FileThumb;
